import {
  IMAGE_ORIENTATION_KEYWORDS,
  mapToLRAPIS,
} from "../enums/MedicalImagesEnum";

/**
 * Parse orientation data from various formats (string, JSON, mixed)
 * Handles DICOM orientation formats for medical images
 *
 * Examples:
 * - CT/MRI: '{"row_left":"R","column_bottom":"P","first_slice":"S"}'
 * - DX: 'CORONAL {"row_left":"R","column_bottom":"I"}'
 * - Legacy: 'sagittal', 'coronal', 'axial'
 *
 * @param {string} orientationValue - The orientation value from the database
 * @returns {Object} - { plane: string, anatomical: Object, raw: string }
 */
export const parseOrientationData = (orientationValue) => {
  if (!orientationValue || typeof orientationValue !== "string") {
    return { plane: null, anatomical: null, raw: orientationValue };
  }

  const trimmed = orientationValue.trim();
  let prefixPlane = null;
  let jsonPart = null;

  // Try to extract prefix plane (e.g., "CORONAL" or "SAGITTAL")
  const prefixMatch = trimmed.match(/^(CORONAL|SAGITTAL|AXIAL)\s+/i);
  if (prefixMatch) {
    prefixPlane = prefixMatch[1].toUpperCase();
  }

  // Try to parse JSON part
  const jsonMatch = trimmed.match(/\{[^}]+\}/);
  if (jsonMatch) {
    try {
      jsonPart = JSON.parse(jsonMatch[0]);
    } catch (e) {
      // Not valid JSON, ignore
    }
  }

  // If no JSON found but the whole string looks like JSON, try parsing it
  if (!jsonPart && trimmed.startsWith("{")) {
    try {
      jsonPart = JSON.parse(trimmed);
    } catch (e) {
      // Not valid JSON, ignore
    }
  }

  // Determine plane from anatomical directions if we have JSON
  let derivedPlane = null;
  if (jsonPart) {
    derivedPlane = derivePlaneFromAnatomical(jsonPart);
  }

  // Use prefix plane if available, otherwise use derived plane
  const finalPlane = prefixPlane || derivedPlane || resolveOrientationFromKeywords(trimmed);

  return {
    plane: finalPlane ? finalPlane.toLowerCase() : null,
    anatomical: jsonPart,
    raw: orientationValue,
  };
};

/**
 * Derive imaging plane from anatomical orientation codes
 * Based on DICOM Patient Orientation conventions
 *
 * @param {Object} anatomical - Object with row_left, column_bottom, first_slice
 * @returns {string|null} - "CORONAL", "SAGITTAL", or "AXIAL"
 */
const derivePlaneFromAnatomical = (anatomical) => {
  if (!anatomical) return null;

  const rowLeft = anatomical.row_left ? mapToLRAPIS(anatomical.row_left) : null;
  const columnBottom = anatomical.column_bottom ? mapToLRAPIS(anatomical.column_bottom) : null;
  const firstSlice = anatomical.first_slice ? mapToLRAPIS(anatomical.first_slice) : null;

  // Collect all available directions
  const directions = [rowLeft, columnBottom, firstSlice].filter(Boolean);

  if (directions.length === 0) return null;

  const dirSet = new Set(directions);

  // CORONAL plane: Left-Right (L/R) with Inferior-Superior (I/S)
  // Looking from front or back
  if ((dirSet.has("L") || dirSet.has("R")) && (dirSet.has("I") || dirSet.has("S"))) {
    return "CORONAL";
  }

  // SAGITTAL plane: Anterior-Posterior (A/P) with Inferior-Superior (I/S)
  // Looking from left or right side
  if ((dirSet.has("A") || dirSet.has("P")) && (dirSet.has("I") || dirSet.has("S"))) {
    return "SAGITTAL";
  }

  // AXIAL plane: Left-Right (L/R) with Anterior-Posterior (A/P)
  // Looking from top or bottom
  if ((dirSet.has("L") || dirSet.has("R")) && (dirSet.has("A") || dirSet.has("P"))) {
    return "AXIAL";
  }

  return null;
};

/**
 * Resolve orientation from keywords (fallback method)
 * Used when no anatomical orientation data is available
 *
 * @param {string} value - The orientation string to parse
 * @returns {string|null} - Orientation keyword or null
 */
const resolveOrientationFromKeywords = (value) => {
  if (!value) return null;
  const lowerValue = value.toLowerCase();
  for (const [orientation, keywords] of Object.entries(IMAGE_ORIENTATION_KEYWORDS)) {
    if (keywords.some(keyword => lowerValue.includes(keyword))) {
      return orientation;
    }
  }
  return null;
};

/**
 * Get a human-readable orientation description
 *
 * @param {Object} parsedOrientation - Result from parseOrientationData
 * @returns {string} - Human-readable description
 */
export const getOrientationDescription = (parsedOrientation) => {
  if (!parsedOrientation) return "";

  const { plane, anatomical } = parsedOrientation;

  if (plane && anatomical) {
    const parts = [];
    if (anatomical.row_left) parts.push(`Row: ${anatomical.row_left}`);
    if (anatomical.column_bottom) parts.push(`Col: ${anatomical.column_bottom}`);
    if (anatomical.first_slice) parts.push(`Slice: ${anatomical.first_slice}`);

    return `${plane.charAt(0).toUpperCase() + plane.slice(1)} (${parts.join(", ")})`;
  }

  if (plane) {
    return plane.charAt(0).toUpperCase() + plane.slice(1);
  }

  return "";
};
